import React from 'react';
import PropTypes from 'prop-types';
import { LuPlus, LuX } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import IconButton from '../../../components/ui/IconButton';
import { useTranslation } from '../../../i18n/LanguageContext';
import { SelectInput } from './FormControls';
import { LANGUAGE_LEVEL_OPTIONS, LANGUAGE_OPTIONS, languageLabel, languageLevelLabel } from './employeeFormOptions';

/**
 * Language + level pairs (`entries` = [{ language, level }]). Saved as the
 * same string as before via formatLanguageInfo; values outside the lists
 * (older free-text entries) stay selectable as "(liste dışı)".
 */
const LanguageEditor = ({ idPrefix, entries, onChange }) => {
  const { t } = useTranslation();
  const update = (index, patch) => onChange(entries.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
  const remove = (index) => onChange(entries.filter((_, i) => i !== index));
  const add = () => onChange([...entries, { language: '', level: '' }]);

  const languageOptions = LANGUAGE_OPTIONS.map((value) => ({ value, label: languageLabel(t, value) }));
  const levelOptions = LANGUAGE_LEVEL_OPTIONS.map((value) => ({ value, label: languageLevelLabel(t, value) }));

  return (
    <div className="tw-space-y-2">
      {entries.length === 0 && (
        <p className="tw-rounded-xl tw-border tw-border-dashed tw-border-slate-300 tw-px-3.5 tw-py-2.5 tw-text-sm tw-text-slate-500 dark:tw-border-white/15 dark:tw-text-slate-400">
          {t('employees.form.languages.empty')}
        </p>
      )}
      {entries.map((entry, index) => (
        // Rows have no stable id; index keys, as in the original editor
        <div key={index} className="tw-grid tw-grid-cols-[minmax(0,1fr)_minmax(0,8rem)_auto] tw-items-center tw-gap-2">
          <SelectInput
            id={index === 0 ? `${idPrefix}-languageInfo` : undefined}
            aria-label={t('employees.form.languages.language')}
            value={entry.language}
            onChange={(event) => update(index, { language: event.target.value })}
            placeholder={t('employees.form.languages.chooseLanguage')}
            options={languageOptions}
            current={entry.language}
          />
          <SelectInput
            aria-label={t('employees.form.languages.level')}
            value={entry.level}
            onChange={(event) => update(index, { level: event.target.value })}
            placeholder={t('employees.form.languages.chooseLevel')}
            options={levelOptions}
            current={entry.level}
          />
          <IconButton label={t('employees.form.languages.remove')} tone="alert" onClick={() => remove(index)}>
            <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
          </IconButton>
        </div>
      ))}
      <Button size="sm" variant="ghost" onClick={add} className="tw-text-teal-700 dark:tw-text-teal-300">
        <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
        {t('employees.form.languages.add')}
      </Button>
    </div>
  );
};

LanguageEditor.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  entries: PropTypes.arrayOf(PropTypes.shape({ language: PropTypes.string, level: PropTypes.string })).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default LanguageEditor;
